'use client'

import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { imagesSizesByLayout } from 'plp/constants/grid'
import { PlpProvider } from 'plp/context/PlpContext'
import { getHasServerFilters } from 'plp/hooks/useFilters/utils'
import type {
	CatalogGroup,
	CatalogPagesConfig,
	VirtualGrid,
} from 'plp/types/Catalog'
import type { ListProps } from 'plp/types/List'
import { ProductCardConsumerLayoutProvider } from 'product-card/context/ProductCardConsumerLayoutContext'
import { ProductCardImagesSizesProvider } from 'product-card/context/ProductCardImagesSizesContext'
import type { ImagesSizesLayout } from 'product-card/context/ProductCardImagesSizesContext/ProductCardImagesSizesContext'
import { ColumnLayout } from 'product-card/types'

import { Grid } from './Grid/Grid'
import { Pagination } from './Pagination/Pagination'
import { StickyBar } from './StickyBar/StickyBar'

function getTotalOf(groups: CatalogGroup[]) {
	return groups
		.map((group) => group.items.length)
		.reduce((accumulator, group) => accumulator + group, 0)
}

// Number of products provided from server
function getLimit(isMobile: boolean, config: CatalogPagesConfig) {
	return isMobile ? config.mobileServerProducts : config.desktopServerProducts
}

function productCardImagesSizes(isMobile: boolean): ImagesSizesLayout {
	return imagesSizesByLayout[
		isMobile ? ColumnLayout.OneColumn : ColumnLayout.FourColumns
	]
}

export function List({
	catalogGroups,
	serverProducts,
	catalogItemsData,
	country,
	language,
	config,
	defaultFilter,
	shouldRenderFilters,
	catalogId,
	filtersData,
	seoId,
}: ListProps): JSX.Element {
	const { isMobile } = useMasterData()

	const firstGroup: VirtualGrid = {
		fromServer: [],
		onClient: [],
		countToRender: 0,
	}

	const restOfGroups: VirtualGrid[] = []

	const hasServerFilters = getHasServerFilters(defaultFilter)
	const initialColumnsLayout = isMobile
		? ColumnLayout.OneColumn
		: ColumnLayout.FourColumns

	let limit = getLimit(isMobile, config)

	catalogGroups.forEach((group) => {
		const { items } = group
		if (limit) {
			const clientItems = items.slice(limit)
			firstGroup.fromServer = serverProducts
			firstGroup.onClient = clientItems
			limit -= Math.min(limit, items.length)
		} else {
			restOfGroups.push({
				title: group.title,
				subtitle: group.subtitle,
				onClient: group.items,
				countToRender: 0,
			})
		}
	})

	const total = getTotalOf(catalogGroups)

	return (
		<ProductCardImagesSizesProvider
			initialImageSizes={productCardImagesSizes(isMobile)}
		>
			<ProductCardConsumerLayoutProvider
				initialColumnsLayout={initialColumnsLayout}
			>
				<PlpProvider
					catalogId={catalogId}
					seoId={seoId}
					country={country}
					language={language}
				>
					{({ gridCssVariables }) => (
						<div id='grid-container' style={gridCssVariables}>
							<StickyBar
								catalogGroups={catalogGroups}
								filtersData={filtersData}
								defaultFilter={defaultFilter}
								hasServerFilters={hasServerFilters}
								shouldRenderFilters={shouldRenderFilters}
							/>
							<Pagination total={total} perPage={config.clientPerPage}>
								{(count, showAsSmall, pendingScrollSlot) => {
									firstGroup.countToRender = Math.min(
										firstGroup.onClient.length,
										count
									)
									let listCountLimit = count - firstGroup.countToRender
									const revalidatedRestOfGroups = restOfGroups.map((group) => {
										const minToRender = Math.min(
											listCountLimit,
											group.onClient.length
										)
										const revalidated = {
											...group,
											countToRender: minToRender,
										}
										listCountLimit -= minToRender
										return revalidated
									})
									return (
										<>
											<Grid
												catalogItemsData={catalogItemsData}
												pendingScrollSlot={pendingScrollSlot}
												group={firstGroup}
												isMobile={isMobile}
												count={count}
												showAsSmall={showAsSmall}
											/>
											{revalidatedRestOfGroups
												.map((group, index) => ({
													...group,
													id: `group-${index}`,
												}))
												.map((group) => (
													<Grid
														key={group.id}
														pendingScrollSlot={pendingScrollSlot}
														catalogItemsData={catalogItemsData}
														group={group}
														isMobile={isMobile}
														count={count}
														showAsSmall={showAsSmall}
													/>
												))}
										</>
									)
								}}
							</Pagination>
						</div>
					)}
				</PlpProvider>
			</ProductCardConsumerLayoutProvider>
		</ProductCardImagesSizesProvider>
	)
}
